import { Box, CircularProgress, Typography, IconButton } from '@mui/material'
import { NavigateBefore, NavigateNext } from '@mui/icons-material'
import React, { useEffect, useState, useMemo } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

interface DocumentPreviewModalProps {
  onLoadPreview: () => Promise<Blob>
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ onLoadPreview }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true)
        setError(null)

        const blobData = await onLoadPreview()

        // Create blob URL (simpler and avoids ArrayBuffer detached issue)
        const blob = new Blob([blobData], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        setPdfUrl(url)
      } catch (err: any) {
        console.error('Error loading preview:', err)
        setError(err.message || 'Er is een fout opgetreden bij het laden van de voorvertoning')
      } finally {
        setLoading(false)
      }
    }

    loadPreview()

    // Cleanup: revoke blob URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [onLoadPreview])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
  }

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1))
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1))

  // Memoize file object to prevent unnecessary reloads
  const file = useMemo(() => (pdfUrl ? { url: pdfUrl } : null), [pdfUrl])

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '80vh',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* PDF Navigation Controls */}
      {numPages && numPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            padding: 2,
            borderBottom: '1px solid #e0e0e0',
          }}>
          <IconButton
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            size="small">
            <NavigateBefore />
          </IconButton>
          <Typography variant="body2">
            Pagina {pageNumber} van {numPages}
          </Typography>
          <IconButton
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            size="small">
            <NavigateNext />
          </IconButton>
        </Box>
      )}

      {/* PDF Viewer */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: 2,
        }}>
        {file && (
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            }
            error={
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="error">
                  Fout bij het laden van de PDF
                </Typography>
              </Box>
            }>
            <Page
              pageNumber={pageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={Math.min(window.innerWidth * 0.8, 800)}
            />
          </Document>
        )}
      </Box>
    </Box>
  )
}
